import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import {
  Users,
  Clock,
  Award,
  ShieldAlert,
  Music,
  Calendar,
  User,
  RefreshCw,
  Download,
} from 'lucide-react';
import {
  fetchPostHogMetrics,
  fetchContentPlaybackDurations,
  fetchPostHogPremiumUsers,
  IAnalyticsData,
  IContentPlaybackDuration,
  IPostHogPremiumUser,
} from '@/store/api/posthogAnalytics';
import { DataTable } from '@/components/common/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { ComboInput } from '@/components/common/ComboInput';
import { DateRangePicker, DateRange } from '@/components/common/DateRangePicker';
import { useGetContentsQuery } from '@/store/api/contentApi';

const COLORS = ['#8B5CF6', '#3B82F6'];
const ContentColors = ['#e8c307', '#827f6f'];

const formatDuration = (seconds: number) => {
  if (seconds === 0) return '0s';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.round(seconds % 60);

  const parts = [];
  if (hrs > 0) parts.push(`${hrs}h`);
  if (mins > 0) parts.push(`${mins}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
};

const normalizeTitle = (title: string) =>
  title
    .toLowerCase()
    .replace(/['"“”’‘]/g, '')
    .trim();

const getDefault7DaysRange = (): DateRange => {
  const from = new Date();
  from.setDate(from.getDate() - 6);
  from.setHours(0, 0, 0, 0);
  const to = new Date();
  to.setHours(23, 59, 59, 999);
  return { from, to };
};

export default function AnalyticsPage() {
  const loadingRef = useRef(false);
  const [data, setData] = useState<IAnalyticsData | null>(null);
  const [playbackData, setPlaybackData] = useState<IContentPlaybackDuration[]>([]);
  const [premiumUsersList, setPremiumUsersList] = useState<IPostHogPremiumUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [playbackLoading, setPlaybackLoading] = useState(true);
  const [premiumLoading, setPremiumLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [premiumSearchQuery, setPremiumSearchQuery] = useState('');
  const [contentTier, setContentTier] = useState<string>('');
  const [contentDateRange, setContentDateRange] = useState<DateRange>(getDefault7DaysRange());
  const [premiumDateRange, setPremiumDateRange] = useState<DateRange>(getDefault7DaysRange());
  const [contentLimit, setContentLimit] = useState<number>(100);
  const [premiumLimit, setPremiumLimit] = useState<number>(100);

  const { data: contentsData } = useGetContentsQuery({ take: 1000 } as any);

  const contentLockMap = useMemo(() => {
    const map = new Map<string, boolean>();
    if (contentsData?.data) {
      contentsData.data.forEach((content) => {
        const isLocked = content.category?.isLocked || false;
        map.set(normalizeTitle(content.title), isLocked);
      });
    }
    return map;
  }, [contentsData]);

  const handleContentTierChange = useCallback((val: string) => {
    setContentTier(val);
    setPage(1);
  }, []);

  // Pagination states
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [premiumPage, setPremiumPage] = useState(1);
  const [premiumTake, setPremiumTake] = useState(10);

  const loadMetricsData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchPostHogMetrics(contentDateRange);
      setData(res);
    } catch (e) {
      setError('Metrics failed to load.');
    } finally {
      setLoading(false);
    }
  };

  const loadContentData = async () => {
    setPlaybackLoading(true);
    try {
      const res = await fetchContentPlaybackDurations(contentDateRange, contentLimit);
      setPlaybackData(res);
    } catch (e) {
      console.error(e);
    } finally {
      setPlaybackLoading(false);
    }
  };

  const loadPremiumData = async () => {
    setPremiumLoading(true);
    try {
      const res = await fetchPostHogPremiumUsers(premiumDateRange, premiumLimit);
      setPremiumUsersList(res);
    } catch (e) {
      console.error(e);
    } finally {
      setPremiumLoading(false);
    }
  };

  const loadAllData = async () => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setError(null);
    await Promise.allSettled([loadMetricsData(), loadContentData(), loadPremiumData()]);
    loadingRef.current = false;
  };

  useEffect(() => {
    loadMetricsData();
  }, [contentDateRange]);

  useEffect(() => {
    loadContentData();
  }, [contentDateRange, contentLimit]);

  useEffect(() => {
    loadPremiumData();
  }, [premiumDateRange, premiumLimit]);

  const handleTakeChange = React.useCallback((val: number) => {
    setTake(val);
    setPage(1);
  }, []);

  const handleSearchChange = React.useCallback((val: string) => {
    setSearchQuery(val);
    setPage(1);
  }, []);

  const handlePremiumTakeChange = React.useCallback((val: number) => {
    setPremiumTake(val);
    setPremiumPage(1);
  }, []);

  const handlePremiumSearchChange = React.useCallback((val: string) => {
    setPremiumSearchQuery(val);
    setPremiumPage(1);
  }, []);

  const totalUsers = (data?.premiumCount || 0) + (data?.freeCount || 0);
  const premiumUsers = data?.premiumCount || 0;
  const freeUsers = data?.freeCount || 0;

  const premiumListening = data?.premiumListeningSeconds || 0;
  const freeListening = data?.freeListeningSeconds || 0;
  const totalListening = premiumListening + freeListening;

  const premiumListeningPercent =
    totalListening > 0 ? ((premiumListening / totalListening) * 100).toFixed(1) : '0.0';
  const freeListeningPercent =
    totalListening > 0 ? ((freeListening / totalListening) * 100).toFixed(1) : '0.0';

  const chartData = data?.chartData || [];

  const listeningChartData = useMemo(
    () => [
      { name: 'Premium Content', value: parseFloat(premiumListeningPercent) },
      { name: 'Free Content', value: parseFloat(freeListeningPercent) },
    ],
    [premiumListeningPercent, freeListeningPercent]
  );

  const filteredPlayback = useMemo(() => {
    return playbackData.filter((item) => {
      const matchesSearch = item.song.toLowerCase().includes(searchQuery.toLowerCase());
      if (!matchesSearch) return false;

      if (!contentTier) return true;
      const isLocked = contentLockMap.get(normalizeTitle(item.song)) || false;
      if (contentTier === 'premium') {
        return isLocked;
      } else if (contentTier === 'free') {
        return !isLocked;
      }
      return true;
    });
  }, [playbackData, searchQuery, contentTier, contentLockMap]);

  const filteredPremiumUsers = useMemo(() => {
    return premiumUsersList.filter(
      (u) =>
        u.name.toLowerCase().includes(premiumSearchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(premiumSearchQuery.toLowerCase())
    );
  }, [premiumUsersList, premiumSearchQuery]);

  const handleExportPlaybackCSV = () => {
    if (filteredPlayback.length === 0) return;

    const headers = [
      'S.No',
      'Content',
      'Total Plays',
      'Total Listening Duration (Seconds)',
      'Last Played At',
    ];
    const rows = filteredPlayback.map((item, idx) => [
      idx + 1,
      `"${item.song.replace(/"/g, '""')}"`,
      item.totalPlays,
      item.totalDurationSeconds.toFixed(1),
      item.lastPlayed ? `"${new Date(item.lastPlayed).toLocaleString()}"` : 'N/A',
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' + [headers, ...rows].map((e) => e.join(',')).join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);

    let filename = 'content_consumption_history.csv';
    if (contentTier === 'free') {
      filename = 'free_content_consumption_history.csv';
    } else if (contentTier === 'premium') {
      filename = 'premium_content_consumption_history.csv';
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const premiumColumns = useMemo<ColumnDef<IPostHogPremiumUser>[]>(
    () => [
      {
        id: 'index',
        header: 'S.No',
        cell: ({ row }) => (
          <div className="text-[#1E0036] font-medium">
            {row.index + 1 + (premiumPage - 1) * premiumTake}
          </div>
        ),
      },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <div className="font-semibold text-[#1E0036] min-w-[150px]">{row.getValue('name')}</div>
        ),
      },
      {
        accessorKey: 'email',
        header: 'Email',
        cell: ({ row }) => <div className="text-[#62556B]">{row.getValue('email')}</div>,
      },
    ],
    [premiumPage, premiumTake]
  );

  const columns = useMemo<ColumnDef<IContentPlaybackDuration>[]>(
    () => [
      {
        id: 'index',
        header: 'S.No',
        cell: ({ row }) => (
          <div className="text-[#1E0036] font-medium">{row.index + 1 + (page - 1) * take}</div>
        ),
      },
      {
        accessorKey: 'song',
        header: 'Content ',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Music className="w-4 h-4 text-[#8B5CF6] shrink-0" />
            <span className="font-semibold text-[#1E0036] min-w-[150px]">
              {row.getValue('song')}
            </span>
          </div>
        ),
      },
      {
        accessorKey: 'totalPlays',
        header: 'Total Plays',
        cell: ({ row }) => {
          const plays = row.getValue('totalPlays') as number;
          return (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100 whitespace-nowrap">
              {plays} plays
            </span>
          );
        },
      },
      {
        accessorKey: 'totalDurationSeconds',
        header: 'Total Listening Duration',
        cell: ({ row }) => {
          const seconds = row.getValue('totalDurationSeconds') as number;
          return (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-[#8B5CF6]/10 text-violet-700 border border-[#8B5CF6]/10">
              <Clock className="w-3.5 h-3.5 mr-1" />
              {formatDuration(seconds)}
            </span>
          );
        },
      },
      {
        accessorKey: 'lastPlayed',
        header: 'Last Played At',
        cell: ({ row }) => {
          const dateStr = row.getValue('lastPlayed') as string;
          return (
            <div className="flex items-center gap-1.5 text-sm text-[#62556B] font-noto ">
              <Calendar className="w-4 h-4 text-violet-400 shrink-0" />
              <span>{dateStr ? new Date(dateStr).toLocaleString() : 'N/A'}</span>
            </div>
          );
        },
      },
    ],
    [page, take]
  );

  if (loading && !data && !playbackData.length && !premiumUsersList.length) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-[#8B5CF6]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#8B5CF6] mb-3" />
      </div>
    );
  }

  if (error && !data && !playbackData.length && !premiumUsersList.length) {
    return (
      <div className="p-6 bg-white min-h-screen text-[#1E0036] font-sans flex flex-col justify-center items-center">
        <div className="bg-white border border-red-200 p-8 rounded-[20px] max-w-md w-full text-center shadow-md">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6 border border-red-100">
            <ShieldAlert className="w-8 h-8 animate-pulse" />
          </div>
          <h2 className="text-xl font-bold text-[#1E0036] mb-2">Telemetry Link Failed</h2>
          <p className="text-[#8E87A4] text-sm mb-6 leading-relaxed">
            Could not retrieve active events from PostHog. Make sure your API key and connection are
            configured correctly.
          </p>
          <div className="p-3 bg-red-50 border border-red-100 rounded-xl mb-6 text-xs text-red-600 font-mono break-all max-h-24 overflow-y-auto">
            {error}
          </div>
          <button
            onClick={loadAllData}
            className="w-full py-3 px-4 bg-[#1E0036] hover:bg-lightPurple text-white rounded-xl font-medium shadow-md active:scale-[0.98] transition-all cursor-pointer"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-2 sm:p-4 min-h-screen space-y-8 font-noto">
      <div className="flex flex-col sm:flex-row items-start justify-between gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#1E0036]">
            Art & Ode Portal Analytics
          </h1>
          <p className="text-[#8E87A4] text-sm mt-1">
            Real-time insights across user signups, physical QR acquisitions, and consumption
            metrics.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">


          

          <button
            onClick={() => loadAllData()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#E5E7EB] bg-white text-[#1E0036] hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={18} />
            Refresh
          </button>
        </div>
      </div>

      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5" />
            <span className="text-sm font-medium">{error}</span>
          </div>
          <button onClick={loadAllData} className="text-xs bg-red-100 hover:bg-red-200 px-3 py-1.5 rounded-lg transition-colors font-semibold">
            Retry
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="bg-white border border-[#E9E9E9] p-6 rounded-[20px] flex items-center gap-4 hover:border-violet-500/50 transition-all shadow-sm">
          <div className="p-4 rounded-xl bg-violet-50 text-violet-600">
            <Users className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[#8E87A4] text-xs font-semibold uppercase tracking-wider">
              Total Users
            </p>
            <h3 className="text-3xl font-bold mt-1 text-[#1E0036]">{totalUsers}</h3>
          </div>
        </div>

        <div className="bg-white border border-[#E9E9E9] p-6 rounded-[20px] flex items-center gap-4 hover:border-emerald-500/50 transition-all shadow-sm">
          <div className="p-4 rounded-xl bg-emerald-50 text-emerald-600">
            <Award className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[#8E87A4] text-xs font-semibold uppercase tracking-wider">
              Premium Users
            </p>
            <h3 className="text-3xl font-bold mt-1 text-emerald-600">{premiumUsers}</h3>
          </div>
        </div>

        <div className="bg-white border border-[#E9E9E9] p-6 rounded-[20px] flex items-center gap-4 hover:border-emerald-500/50 transition-all shadow-sm">
          <div className="p-4 rounded-xl bg-blue-50 text-blue-600">
            <User className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[#8E87A4] text-xs font-semibold uppercase tracking-wider">
              Free Users
            </p>
            <h3 className="text-3xl font-bold mt-1 text-blue-600">{freeUsers}</h3>
          </div>
        </div>

        <div className="bg-white border border-[#E9E9E9] p-6 rounded-[20px] flex items-center gap-4 hover:border-blue-500/50 transition-all shadow-sm">
          <div className="p-4 rounded-xl bg-blue-50 text-blue-600">
            <Clock className="w-6 h-6" />
          </div>
          <div>
            <p className="text-[#8E87A4] text-xs font-semibold uppercase tracking-wider">
              Total Playback Time
            </p>
            <h3 className="text-3xl font-bold mt-1 text-blue-400">
              {data?.totalListeningSeconds !== undefined
                ? formatDuration(data.totalListeningSeconds)
                : '0s'}
            </h3>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white border border-[#E9E9E9] p-6 rounded-[20px] shadow-sm flex flex-col items-center">
          <h2 className="text-lg font-bold text-[#1E0036] self-start mb-6">
            User Tier Distribution
          </h2>
          <div className="w-full h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    borderColor: '#E9E9E9',
                    borderRadius: '12px',
                  }}
                  itemStyle={{ color: '#1E0036' }}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Listening Activity Card */}
        <div className="bg-white border border-[#E9E9E9] p-6 rounded-[20px] shadow-sm flex flex-col items-center">
          <h2 className="text-lg font-bold text-[#1E0036] self-start mb-6">
            Listening Ratio (Free vs Premium)
          </h2>
          <div className="w-full h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={listeningChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {listeningChartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={ContentColors[index % ContentColors.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#FFFFFF',
                    borderColor: '#E9E9E9',
                    borderRadius: '12px',
                  }}
                  itemStyle={{ color: '#1E0036' }}
                  formatter={(value: any, name: any) => [`${value}%`, name]}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div>
        <DataTable
          columns={columns}
          data={filteredPlayback}
          title="Content Consumption History"
          totalCount={filteredPlayback.length}
          page={page}
          take={take}
          loading={playbackLoading}
          onPageChange={setPage}
          onTakeChange={handleTakeChange}
          enableSearch
          enableContentTierFilter
          contentTier={contentTier}
          onContentTierChange={handleContentTierChange}
          search={searchQuery}
          searchPlaceholder="Search by content"
          onSearchChange={handleSearchChange}
          extraActions={
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 mr-2">
                <DateRangePicker
                  value={contentDateRange}
                  onChange={setContentDateRange}
                  className="w-[210px]"
                  align="right"
                />
                <ComboInput 
                  value={contentLimit} 
                  onChange={setContentLimit} 
                  options={[10, 50, 100, 500, 1000]} 
                />
              </div>
              <Button
                type="button"
                onClick={handleExportPlaybackCSV}
                className="w-full sm:w-auto bg-[#1E0036] hover:bg-lightPurple text-white px-5 h-10 rounded-lg transition-all flex items-center gap-2 cursor-pointer"
              >
                <Download className="h-4 w-4" />
                {contentTier === 'free'
                  ? ' Free Content'
                  : contentTier === 'premium'
                    ? ' Premium Content'
                    : 'Export CSV'}
              </Button>
            </div>
          }
        />
      </div>

      <div>
        <DataTable
          columns={premiumColumns}
          data={filteredPremiumUsers}
          title="Premium Users List"
          totalCount={filteredPremiumUsers.length}
          page={premiumPage}
          take={premiumTake}
          loading={premiumLoading}
          onPageChange={setPremiumPage}
          onTakeChange={handlePremiumTakeChange}
          enableSearch
          search={premiumSearchQuery}
          searchPlaceholder="Search by name or email..."
          onSearchChange={handlePremiumSearchChange}
          extraActions={
            <div className="flex items-center gap-2 mr-2">
              <DateRangePicker
                value={premiumDateRange}
                onChange={setPremiumDateRange}
                className="w-[210px]"
                align="right"
              />
              <ComboInput 
                value={premiumLimit} 
                onChange={setPremiumLimit} 
                options={[10, 50, 100, 500, 1000]} 
              />
            </div>
          }
        />
      </div>
    </div>
  );
}
